import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api/client';
import Modal from '../components/shared/Modal';
import EmptyState from '../components/shared/EmptyState';
import { Link2, Plus, Pencil, Trash2, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { cn } from '../utils/cn';

export default function LicenseManagers() {
  const { t } = useTranslation();
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', api_url: '', consumer_key: '', consumer_secret: '', is_active: true });
  const [testResults, setTestResults] = useState({});

  const fetchManagers = useCallback(async () => {
    try {
      const response = await api.get('/license-managers');
      setManagers(response.data);
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchManagers(); }, [fetchManagers]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', api_url: '', consumer_key: '', consumer_secret: '', is_active: true });
    setModalOpen(true);
  };

  const openEdit = (manager) => {
    setEditing(manager);
    setForm({ name: manager.name, api_url: manager.api_url, consumer_key: manager.consumer_key, consumer_secret: '', is_active: manager.is_active });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...form };
      if (editing && !data.consumer_secret) delete data.consumer_secret;

      if (editing) { await api.put(`/license-managers/${editing.id}`, data); } else { await api.post('/license-managers', data); }
      setModalOpen(false);
      fetchManagers();
    } catch (error) { console.error('Save failed:', error); }
  };

  const handleTest = async (id) => {
    setTestResults({ ...testResults, [id]: { loading: true } });
    try {
      const response = await api.post(`/license-managers/${id}/test`);
      setTestResults({ ...testResults, [id]: response.data });
    } catch (error) {
      setTestResults({ ...testResults, [id]: { success: false, message: t('license_managers.test_failed') } });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('license_managers.delete_confirm'))) return;
    try { await api.delete(`/license-managers/${id}`); fetchManagers(); } catch (error) { console.error('Delete failed:', error); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('license_managers.title')}</h1>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">
          <Plus className="h-4 w-4" /> {t('license_managers.add')}
        </button>
      </div>

      {managers.length === 0 ? (
        <EmptyState icon={Link2} title={t('license_managers.no_results')} description={t('license_managers.no_results_hint')} action={<button onClick={openCreate} className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">{t('license_managers.add')}</button>} />
      ) : (
        <div className="space-y-4">
          {managers.map((manager) => (
            <div key={manager.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center', manager.is_active ? 'bg-green-100' : 'bg-gray-100')}>
                    <Link2 className={cn('h-5 w-5', manager.is_active ? 'text-green-600' : 'text-gray-400')} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{manager.name}</h3>
                    <p className="text-sm text-gray-500">{manager.api_url}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => handleTest(manager.id)} className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors" title={t('license_managers.test_connection')}><RefreshCw className="h-4 w-4" /></button>
                  <button onClick={() => openEdit(manager)} className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => handleDelete(manager.id)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-4 text-sm text-gray-500">
                <span>{manager.is_active ? t('common.active') : t('common.inactive')}</span>
                {manager.last_sync && <span>{t('license_managers.last_sync')}: {new Date(manager.last_sync).toLocaleString()}</span>}
              </div>
              {testResults[manager.id] && !testResults[manager.id].loading && (
                <div className="mt-3 flex items-center gap-2 text-sm">
                  {testResults[manager.id].success ? (
                    <><CheckCircle className="h-4 w-4 text-green-500" /> <span className="text-green-700">{t('license_managers.test_success')}</span></>
                  ) : (
                    <><XCircle className="h-4 w-4 text-red-500" /> <span className="text-red-700">{testResults[manager.id].message}</span></>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? t('license_managers.edit_title') : t('license_managers.create_title')}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('license_managers.name')} *</label><input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('license_managers.url')} *</label><input type="url" value={form.api_url} onChange={(e) => setForm({ ...form, api_url: e.target.value })} required className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" placeholder="https://your-store.com" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('license_managers.consumer_key')} *</label><input type="text" value={form.consumer_key} onChange={(e) => setForm({ ...form, consumer_key: e.target.value })} required className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('license_managers.consumer_secret')} {editing ? t('email_accounts.leave_empty_to_keep') : '*'}</label><input type="password" value={form.consumer_secret} onChange={(e) => setForm({ ...form, consumer_secret: e.target.value })} required={!editing} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" /> {t('license_managers.active')}</label>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">{t('common.cancel')}</button>
            <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">{editing ? t('common.update') : t('common.create')}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
